import React, { useEffect, useState } from "react";

const fmt = (val, digits = 2) => {
  const num = Number(val);
  return isNaN(num) ? "0.00" : num.toFixed(digits);
};

// Format date to DD/MM/YYYY (Indian format)
const fmtDate = (dateStr) => {
  if (!dateStr) return "";
  const d = new Date(dateStr);
  if (isNaN(d)) return dateStr;
  return d.toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
};

const RebatePage = ({ fromDate, toDate }) => {
  const [rebateData, setRebateData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // 👇 Fetch username dynamically from localStorage
  const farmerUsername = localStorage.getItem("farmerUsername") || "F-B-3";

  useEffect(() => {
    if (!farmerUsername || !toDate) return;

    const fetchRebate = async () => {
      setLoading(true);
      setError(null);

      try {
        const res = await fetch(`${process.env.REACT_APP_API_URL}/getRebate`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            username: farmerUsername,
            to_date: toDate,
          }),
        });

        if (!res.ok) {
          const errMsg = await res.text();
          throw new Error(`Server error: ${res.status} - ${errMsg}`);
        }

        const json = await res.json();

        if (!json.rebates || json.rebates.length === 0) {
          setRebateData(null);
        } else {
          setRebateData(json.rebates[0]);
        }
      } catch (err) {
        console.error("API Error:", err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchRebate();
  }, [farmerUsername, toDate]);

  if (loading)
    return <p style={{ textAlign: "center" }}>डेटा लोड होत आहे...</p>;

  if (error)
    return (
      <p style={{ textAlign: "center", color: "red" }}>
        त्रुटी आली: {error}
      </p>
    );

  if (!rebateData)
    return (
      <p style={{ textAlign: "center", marginTop: "2rem" }}>
        रिबेटची माहिती उपलब्ध नाही.
      </p>
    );

  return (
    <div
      style={{
        background: "#fff",
        maxWidth: "750px",
        margin: "2rem auto",
        padding: "2rem",
        border: "1px solid #ccc",
        borderRadius: "12px",
        fontFamily: "Mukta, sans-serif",
        color: "#222",
        boxShadow: "0 2px 6px rgba(0,0,0,0.1)",
      }}
    >
      <h2 style={{ textAlign: "center", marginBottom: "0.5rem" }}>
        वार्षिक रिबेट पावती
      </h2>

      <p style={{ textAlign: "center", marginBottom: "1rem" }}>
        कालावधी: {fmtDate(rebateData.FromDate)} ते {fmtDate(rebateData.ToDate)}
      </p>

      {/* --- Main 3-column table (गाय / म्हैस) --- */}
      <table
        style={{
          width: "100%",
          borderCollapse: "collapse",
          marginBottom: "1.5rem",
          fontSize: "0.95rem",
        }}
      >
        <thead>
          <tr style={{ backgroundColor: "#f2f2f2" }}>
            <th style={thLeft}>घटक</th>
            <th style={thCenter}>गाय</th>
            <th style={thCenter}>म्हैस</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td style={tdLeft}>लिटर</td>
            <td style={tdRight}>{fmt(rebateData.CowLiters)}</td>
            <td style={tdRight}>{fmt(rebateData.BuffLiters)}</td>
          </tr>
          <tr>
            <td style={tdLeft}>रिबेट दर प्रति लिटर ₹</td>
            <td style={tdRight}>{fmt(rebateData.CowCommission/rebateData.CowLiters)}</td>
            <td style={tdRight}>{fmt(rebateData.BuffCommission/rebateData.BuffLiters)}</td>
          </tr>
          <tr>
            <td style={tdLeft}>रिबेट रक्कम ₹</td>
            <td style={tdRight}>{fmt(rebateData.CowCommission)}</td>
            <td style={tdRight}>{fmt(rebateData.BuffCommission)}</td>
          </tr>
          <tr>
            <td style={tdLeft}>वार्षिक सरासरी दर / लि. ₹</td>
            <td style={tdRight}>{fmt((rebateData.CowAmount/rebateData.CowLiters)+(rebateData.CowCommission/rebateData.CowLiters))}</td>
            <td style={tdRight}>{fmt((rebateData.BuffAmount/rebateData.BuffLiters)+(rebateData.BuffCommission/rebateData.BuffLiters))}</td>
          </tr>
        </tbody>
      </table>

      {/* --- Summary section --- */}
      <table
        style={{
          width: "100%",
          borderCollapse: "collapse",
          fontSize: "0.95rem",
          marginBottom: "1rem",
        }}
      >
        <tbody>
          <tr>
            <td style={tdLeft}> </td>
            <td style={tdRight}>{fmt(rebateData.Discount)}</td>
          </tr>          
          <tr>
            <td style={tdLeft}>एकूण रिबेट ₹</td>
            <td style={tdRight}>{fmt(rebateData.CurrDepoBal)}</td>
          </tr>
          <tr>
            <td style={tdLeft}>मूळ रिबेट कपात ₹</td>
            <td style={tdRight}>{fmt(rebateData.CurrDepoBal-rebateData.NetBill)}</td>
          </tr>
          <tr>
            <td style={tdLeft}>औषधे  ₹</td>
            <td style={tdRight}>{fmt(rebateData.UchalMinus5)}</td>
          </tr>
          <tr>
            <td style={tdLeft}>A. I. ₹</td>
            <td style={tdRight}>{fmt(rebateData.UchalMinus6)}</td>
          </tr>
          <tr>
            <td style={tdLeft}>खाद्य ₹</td>
            <td style={tdRight}>{fmt(rebateData.UchalMinus2)}</td>
          </tr>
          <tr>
            <td style={tdLeft}>उचल ₹</td>
            <td style={tdRight}>{fmt(rebateData.UchalMinus3)}</td>
          </tr>          
          <tr>
            <td style={tdLeft}>दूध विक्री ₹</td>
            <td style={tdRight}>{fmt(rebateData.UchalMinus4)}</td>
          </tr>          
          <tr style={{ backgroundColor: "#f9f9f9" }}>
            <td style={{ ...tdLeft, fontWeight: "bold" }}>देय रिबेट ₹</td>
            <td style={{ ...tdRight, fontWeight: "bold" }}>
              ₹{fmt(rebateData.NetBill)}
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  );
};

// --- Styling ---
const thLeft = {
  border: "1px solid #ccc",
  padding: "8px",
  textAlign: "left",
  backgroundColor: "#f2f2f2",
};

const thCenter = {
  border: "1px solid #ccc",
  padding: "8px",
  textAlign: "center",
  backgroundColor: "#f2f2f2",
};

const tdLeft = {
  border: "1px solid #ccc",
  padding: "8px",
  textAlign: "left",
  width: "50%",
};

const tdRight = {
  border: "1px solid #ccc",
  padding: "8px",
  textAlign: "right",
  width: "25%",
};

export default RebatePage;
